import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Attachment,
  Button,
  Channel,
  CodeChip,
  DocumentStage,
  EmptyState,
  EntityCell,
  Field,
  FieldGrid,
  FieldValue,
  FilterBar,
  FilterSearch,
  Form,
  FormSection,
  Icon,
  IdentityCard,
  LoadingState,
  MailBody,
  MailBrand,
  MailEnvelope,
  MailField,
  MailFooter,
  MailSummary,
  MailText,
  MetaSeparator,
  MetaStrong,
  PageHeader,
  Panel,
  PanelFoot,
  PanelHead,
  PanelNote,
  PanelTag,
  RecordsCard,
  RecordsCount,
  RecordsFooter,
  RecordsScroll,
  RowAction,
  RowActions,
  RowDetail,
  SelectField,
  Split,
  SplitMain,
  SplitSide,
  StatusChip,
  SummaryBand,
  SummaryCard,
  TabStrip,
  Table,
  Td,
  TextArea,
  TextField,
  Th,
  Timeline,
  TimelineEvent,
  Tr,
  ViewerBar,
  ViewerGroup,
  statusTone,
} from '@tessma/design-system';
import type { AccentTone, ChipTone } from '@tessma/design-system';
import { Refusal, refusalText, formatDay, formatMoney, formatWhen, useAsync } from '../ui/async.js';
import { fractionOf, initialsOf, kilobytes } from './figures.js';
import type {
  CustomerSummary,
  FinanceCustomer,
  FinanceGateway,
  FinancePaymentTerm,
  Invoice,
  InvoiceDelivery,
  InvoiceDocument,
  PartyAddress,
  PartyIdentifier,
  SettlementDocument,
} from './gateway.js';

/**
 * SCREENS 03, 31, 32 AND 33 — the customer, the document, the send, the register.
 *
 * **THE STRUCTURE IS THE REFERENCE'S**, converted from `03_Customer_Detail`,
 * `31_PDF_Preview`, `32_Send_Invoice` and `33_Delivery_History`. Each is the
 * markup's own anatomy — an identity header, a summary band of the size it
 * draws, a twelve-column split, the panels in the order it puts them — built
 * from `@tessma/design-system/records`, where every rule names the Tailwind
 * class it transcribes.
 *
 * **THE SHELL NAMES THE PAGE.** No `<h1>` appears here; the breadcrumb, the
 * heading and the subtitle all come from the route table. What these screens
 * carry is the RECORD's identity, which is a different fact.
 *
 * **WHAT THESE FOUR SHARE, and the reason they are one file:** every one of them
 * is about a document leaving the building, and every one of them has a sentence
 * it is not allowed to say.
 *
 * **NOTHING HERE SAYS `delivered`.** The platform records that a mail server
 * accepted a message; it has never observed a mailbox. So `sent` is labelled
 * *handed to mail server*, and the word "delivered" appears in no label, chip or
 * caption on any of these screens. The Stitch design for Screen 33 shows read
 * receipts, PEPPOL acknowledgements and open-banking settlement pings — every
 * one of those is a fact nothing in this system observes, so they are omitted
 * rather than drawn (§4E: never a dead or fake action).
 *
 * **NOTHING HERE DOES ARITHMETIC ON MONEY.** Screen 03's figures are summed in
 * SQL by `/finance/customers/{id}/summary`, and deliberately NOT from the
 * invoice list on the same screen — a total over the rows shown would quietly
 * mean "of the ones listed". The one derived number is a bar's width.
 *
 * **WHAT ELSE WAS OMITTED FROM THE REFERENCE, AND WHY.** Screen 03's AI credit
 * assessment, settlement-velocity chart, stock headshot and account-director
 * card: no scoring model, no time series, no photographs and no relationship
 * records exist. Screen 31's PDF/A conformance panel, QSealC signature, print
 * pre-flight metrics and Factur-X payload: the renderer makes none of those
 * claims, and its real integrity facts — file name, size and SHA-256 — take
 * their place. Screen 32's PEPPOL channel, customer portal, template presets,
 * open-banking button and dunning schedule: none is built. Screen 33's PEPPOL
 * parity, settlement velocity, read receipts and cryptographic proof-of-delivery
 * download: none is observed.
 */

function money(value: string | null, currency: string | null): string {
  return value === null ? '—' : formatMoney(value, currency ?? 'GBP');
}

/**
 * A delivery's state, in words that claim exactly what happened.
 *
 * This is the only place in the browser that turns the state into prose, which
 * makes it the only place that could start overclaiming. `sent` is not
 * "delivered", and the label says so.
 */
const DELIVERY_STATE: Readonly<Record<InvoiceDelivery['state'], string>> = {
  queued: 'Queued',
  sent: 'Handed to mail server',
  failed: 'Failed',
};

const DELIVERY_TONE: Readonly<Record<InvoiceDelivery['state'], ChipTone>> = {
  queued: 'info',
  sent: 'success',
  failed: 'danger',
};

const DELIVERY_ACCENT: Readonly<Record<InvoiceDelivery['state'], AccentTone>> = {
  queued: 'info',
  sent: 'success',
  failed: 'danger',
};

function addressLine(address: PartyAddress): string {
  return [address.line1, address.line2, address.city, address.postcode, address.countryCode]
    .filter((part): part is string => part !== null && part.trim() !== '')
    .join(', ');
}

// ---- Screen 03: customer detail ---------------------------------------------

/**
 * THE CUSTOMER, AS FINANCE HOLDS THEM — `03_Customer_Detail/code.html`.
 *
 * The reference's anatomy: a context header carrying the customer's own name and
 * standing, four operational KPI cards, then an eight/four split with the
 * tabbed ledger on the left and the billing profile on the right.
 *
 * THE ADDRESS PANEL SAYS WHEN THERE IS NO ADDRESS, loudly. "This customer has no
 * address recorded" is the thing somebody needs to know BEFORE they try to send
 * an invoice, so an empty panel that says why beats a panel that is not there.
 */
export function CustomerDetailScreen({
  gateway,
  partyId,
  legalEntityId,
  canManageParty,
  onOpenInvoice,
  onNewInvoice,
}: {
  readonly gateway: FinanceGateway;
  readonly partyId: string;
  readonly legalEntityId: string;
  readonly canManageParty: boolean;
  readonly onOpenInvoice: (invoiceId: string) => void;
  readonly onNewInvoice: () => void;
}) {
  const { state, reload } = useAsync<{
    readonly customer: FinanceCustomer | null;
    readonly summary: CustomerSummary;
    readonly invoices: readonly Invoice[];
    readonly receipts: readonly SettlementDocument[];
    readonly addresses: readonly PartyAddress[];
    readonly identifiers: readonly PartyIdentifier[];
    readonly terms: readonly FinancePaymentTerm[];
  }>(async () => {
    const [customers, summary, invoices, details, receipts, terms] = await Promise.all([
      gateway.customers(legalEntityId),
      gateway.customerSummary(partyId, legalEntityId),
      gateway.invoices(legalEntityId),
      gateway.partyDetails(partyId),
      gateway.receipts(legalEntityId).catch(() => [] as readonly SettlementDocument[]),
      gateway.paymentTerms(legalEntityId).catch(() => [] as readonly FinancePaymentTerm[]),
    ]);
    return {
      customer: customers.find((entry) => entry.partyId === partyId) ?? null,
      summary,
      invoices: invoices.filter((invoice) => invoice.partyId === partyId),
      receipts: receipts.filter((receipt) => receipt.partyId === partyId),
      addresses: details.addresses,
      identifiers: details.identifiers,
      terms,
    };
  }, [gateway, partyId, legalEntityId]);

  const [tab, setTab] = useState('invoices');

  if (state.status === 'loading') return <LoadingState title="Loading customer…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { customer, summary, invoices, receipts, addresses, identifiers, terms } = state.value;
  if (customer === null) {
    return (
      <EmptyState
        icon="users-three"
        title="No such customer here"
        description="Either this customer has no finance profile in this legal entity, or it is outside your access. The two are deliberately the same answer."
      />
    );
  }

  const currency = summary.currency ?? customer.billingCurrency;
  const term = terms.find((entry) => entry.id === customer.paymentTermId);
  const vat = identifiers.find((entry) => entry.identifierKind === 'vat_number');
  const company = identifiers.find((entry) => entry.identifierKind === 'company_number');

  const tabs = [
    { id: 'invoices', label: 'Invoices', count: invoices.length },
    { id: 'receipts', label: 'Receipts', count: receipts.length },
  ];

  return (
    <>
      <PageHeader
        actions={
          <Button variant="primary" icon={<Icon name="plus" size={14} />} onClick={onNewInvoice}>
            Create invoice
          </Button>
        }
      />

      <IdentityCard
        mark={initialsOf(customer.displayName)}
        name={customer.displayName}
        {...(company === undefined ? {} : { code: company.value })}
        chips={
          customer.isOnHold ? (
            <StatusChip tone="warning">On credit hold</StatusChip>
          ) : customer.isActive ? (
            <StatusChip tone="success">Active</StatusChip>
          ) : (
            <StatusChip tone="neutral">Inactive</StatusChip>
          )
        }
        meta={
          <>
            <span>Terms</span>
            <MetaStrong>{term?.name ?? 'Entity default'}</MetaStrong>
            <MetaSeparator />
            <span>Billing currency</span>
            <MetaStrong>{customer.billingCurrency ?? 'Entity default'}</MetaStrong>
            {vat !== undefined && (
              <>
                <MetaSeparator />
                <span>VAT</span>
                <MetaStrong>{vat.value}</MetaStrong>
              </>
            )}
          </>
        }
      />

      {/*
        THE FOUR CARDS STITCH DEFINES, FILLED WITH THE SERVER'S FIGURES. The
        fourth is the reference's credit-limit utilisation: the limit and the
        outstanding are both the server's, and the only thing worked out here is
        the bar's width — exactly, in BigInt, and never shown as a number.
      */}
      <SummaryBand>
        <SummaryCard
          icon="bank"
          tone="teal"
          label="Total billed"
          value={money(summary.totalBilled, currency)}
          caption="Since the first issued"
        />
        <SummaryCard
          icon="calendar-blank"
          tone="info"
          label="Outstanding balance"
          value={money(summary.outstanding, currency)}
          caption={`${String(summary.openInvoices)} still owing`}
        />
        <SummaryCard
          icon="warning"
          urgent
          label="Overdue exposure"
          value={money(summary.overdue, currency)}
          caption={
            summary.oldestOverdueNumber === null
              ? 'Nothing past due'
              : `${String(summary.oldestOverdueDays ?? 0)}d overdue · ${summary.oldestOverdueNumber}`
          }
        />
        {customer.creditLimit === null ? (
          <SummaryCard
            icon="lock-simple"
            tone="neutral"
            label="Credit limit"
            unavailable="No credit limit is set for this customer."
          />
        ) : (
          <SummaryCard
            icon="lock-simple"
            tone="teal"
            label="Credit limit"
            value={money(customer.creditLimit, currency)}
            fraction={fractionOf(summary.outstanding, customer.creditLimit)}
            caption={`${money(summary.outstanding, currency)} used · ${customer.creditPolicy}`}
          />
        )}
      </SummaryBand>

      <Split>
        <SplitMain span={8}>
          <RecordsCard>
            <TabStrip label="Customer ledger" tabs={tabs} active={tab} onSelect={setTab} />
            {tab === 'invoices' ? (
              invoices.length === 0 ? (
                <EmptyState
                  icon="tray"
                  title="No invoices yet"
                  description="Nothing has been raised against this customer in this legal entity."
                  compact
                />
              ) : (
                <>
                  {/*
                    FIVE COLUMNS IN A PANEL EIGHT OF TWELVE WIDE. The net and the
                    VAT are on the invoice's own page; here the question is what
                    was billed, when it was due and whether it is settled.
                  */}
                  <RecordsScroll dense>
                    <Table label="Invoices raised against this customer">
                      <thead>
                        <Tr>
                          <Th>Invoice #</Th>
                          <Th>Issue date</Th>
                          <Th>Due date</Th>
                          <Th align="right">Total</Th>
                          <Th>Status</Th>
                        </Tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice) => (
                          <Tr key={invoice.id}>
                            <Td>
                              <EntityCell
                                name={invoice.invoiceNumber ?? 'Draft'}
                                {...(invoice.customerReference === null
                                  ? {}
                                  : { sub: invoice.customerReference })}
                                onOpen={() => {
                                  onOpenInvoice(invoice.id);
                                }}
                              />
                            </Td>
                            <Td variant="secondary">{formatDay(invoice.issueDate)}</Td>
                            <Td variant="secondary">{formatDay(invoice.dueDate)}</Td>
                            <Td variant="money">{money(invoice.grossTotal, invoice.currency)}</Td>
                            <Td>
                              <StatusChip tone={statusTone(invoice.status)}>
                                {invoice.status}
                              </StatusChip>
                            </Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  </RecordsScroll>
                  <RecordsFooter>
                    <span>
                      Showing <RecordsCount>{invoices.length}</RecordsCount> invoices raised in this
                      legal entity
                    </span>
                    <span>Newest issue date first</span>
                  </RecordsFooter>
                </>
              )
            ) : receipts.length === 0 ? (
              <EmptyState
                icon="bank"
                title="No receipts"
                description="No money has been received from this customer in this legal entity."
                compact
              />
            ) : (
              <>
                <RecordsScroll dense>
                  <Table label="Receipts from this customer">
                    <thead>
                      <Tr>
                        <Th>Number</Th>
                        <Th>Received</Th>
                        <Th align="right">Amount</Th>
                        <Th align="right">Applied</Th>
                        <Th align="right">Remaining</Th>
                      </Tr>
                    </thead>
                    <tbody>
                      {receipts.map((receipt) => (
                        <Tr key={receipt.id}>
                          <Td>{receipt.number ?? '—'}</Td>
                          <Td variant="secondary">{formatDay(receipt.occurredOn)}</Td>
                          <Td variant="money">{money(receipt.amount, receipt.currency)}</Td>
                          <Td variant="money">{money(receipt.allocated, receipt.currency)}</Td>
                          <Td variant="money">{money(receipt.remaining, receipt.currency)}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </RecordsScroll>
                <RecordsFooter>
                  <span>
                    Showing <RecordsCount>{receipts.length}</RecordsCount> receipts
                  </span>
                  <span>Applied amounts are the server&rsquo;s own allocations</span>
                </RecordsFooter>
              </>
            )}
          </RecordsCard>
        </SplitMain>

        <SplitSide span={4}>
          <Panel>
            <PanelHead title="Contact &amp; billing profile" icon="identification-badge" />
            <FieldGrid columns={2}>
              <Field label="Billing email" span2>
                <FieldValue muted={customer.billingEmail === null}>
                  {customer.billingEmail ?? 'Not set — a send will ask for an address'}
                </FieldValue>
              </Field>
              <Field label="Payment terms">
                <FieldValue strong>{term?.name ?? 'Entity default'}</FieldValue>
              </Field>
              <Field label="Billing currency">
                <FieldValue strong>{customer.billingCurrency ?? 'Entity default'}</FieldValue>
              </Field>
              <Field label="VAT registration">
                <FieldValue strong>{vat?.value ?? '—'}</FieldValue>
              </Field>
              <Field label="Company number">
                <FieldValue strong>{company?.value ?? '—'}</FieldValue>
              </Field>
              <Field label="Default tax code" span2>
                <FieldValue>
                  {customer.defaultTaxCode === null ? (
                    '—'
                  ) : (
                    <CodeChip>{customer.defaultTaxCode}</CodeChip>
                  )}
                </FieldValue>
              </Field>
            </FieldGrid>
            <PanelNote icon="info">
              The organisation itself lives in the shared party record, and every module sees the
              same one. Only the terms, the limit and the coding above belong to Finance.
            </PanelNote>
          </Panel>

          <CustomerAddresses
            gateway={gateway}
            partyId={partyId}
            addresses={addresses}
            canManage={canManageParty}
            onSaved={reload}
          />
        </SplitSide>
      </Split>
    </>
  );
}

/**
 * THE ADDRESSES, AND THE FORM THAT FINALLY WRITES ONE.
 *
 * `party_address` has existed since migration 0053 with nothing able to insert a
 * row. This is the form on the other end of that fix, and it lives on the
 * customer screen rather than a settings page because the moment somebody
 * discovers the address is missing is the moment they try to send an invoice.
 */
function CustomerAddresses({
  gateway,
  partyId,
  addresses,
  canManage,
  onSaved,
}: {
  readonly gateway: FinanceGateway;
  readonly partyId: string;
  readonly addresses: readonly PartyAddress[];
  readonly canManage: boolean;
  readonly onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<PartyAddress['addressKind']>('billing');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [postcode, setPostcode] = useState('');
  const [country, setCountry] = useState('GB');
  const [error, setError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  async function save(): Promise<void> {
    setSaving(true);
    setError(undefined);
    try {
      await gateway.savePartyAddress(partyId, {
        addressKind: kind,
        line1,
        ...(line2 === '' ? {} : { line2 }),
        ...(city === '' ? {} : { city }),
        ...(postcode === '' ? {} : { postcode }),
        countryCode: country.toUpperCase(),
        isDefault: true,
      });
      setOpen(false);
      setLine1('');
      setLine2('');
      setCity('');
      setPostcode('');
      onSaved();
    } catch (failure) {
      setError(refusalText(failure, 'The address could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel>
      <PanelHead title="Addresses" icon="folder">
        {canManage && !open && (
          <Button
            variant="tertiary"
            icon={<Icon name="plus" size={13} />}
            onClick={() => {
              setOpen(true);
            }}
          >
            Add address
          </Button>
        )}
      </PanelHead>
      {addresses.length === 0 && !open && (
        <Alert tone="warning" title="No address recorded">
          An invoice states who it is addressed to, so this customer needs a billing or registered
          address before a document sent to them is complete.
        </Alert>
      )}
      {addresses.map((address) => (
        <Field
          key={address.id}
          label={`${address.addressKind}${address.isDefault ? ' · default' : ''}`}
        >
          <FieldValue>{addressLine(address)}</FieldValue>
        </Field>
      ))}
      {open && (
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <FormSection title="New address">
            {error !== undefined && (
              <Alert tone="danger" title="Not saved">
                {error}
              </Alert>
            )}
            <SelectField
              label="Kind"
              value={kind}
              onChange={(event) => {
                setKind(event.target.value as PartyAddress['addressKind']);
              }}
              options={[
                { value: 'billing', label: 'Billing' },
                { value: 'registered', label: 'Registered' },
                { value: 'shipping', label: 'Shipping' },
              ]}
            />
            <TextField
              label="Address line 1"
              value={line1}
              onChange={(event) => {
                setLine1(event.target.value);
              }}
              required
            />
            <TextField
              label="Address line 2"
              value={line2}
              onChange={(event) => {
                setLine2(event.target.value);
              }}
              optional
            />
            <TextField
              label="City"
              value={city}
              onChange={(event) => {
                setCity(event.target.value);
              }}
              optional
            />
            <TextField
              label="Postcode"
              value={postcode}
              onChange={(event) => {
                setPostcode(event.target.value);
              }}
              optional
            />
            <TextField
              label="Country code"
              value={country}
              onChange={(event) => {
                setCountry(event.target.value);
              }}
              helper="ISO 3166-1 alpha-2, such as GB."
              required
            />
          </FormSection>
          <div>
            <Button type="submit" variant="primary" disabled={saving || line1.trim() === ''}>
              {saving ? 'Saving…' : 'Save address'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </Form>
      )}
    </Panel>
  );
}

// ---- Screen 31: the document ------------------------------------------------

/**
 * THE INVOICE AS THE CUSTOMER WILL RECEIVE IT — `31_PDF_Preview/code.html`.
 *
 * The reference's anatomy: an action bar with the document's status badges, then
 * an eight/four split with the viewer toolbar over a paper stage on the left and
 * the inspection panels on the right.
 *
 * **IT IS THE REAL PDF, rendered by the server from the ledger, not a picture of
 * one — and not an HTML redraw of one either.** The reference draws an A4 sheet
 * in markup; doing that here would put a second renderer in the product and the
 * two would disagree the first time one of them changed.
 *
 * THE BYTES ARRIVE BASE64 IN AN AUTHORISED RESPONSE and become a blob here. An
 * `<iframe src="/finance/invoices/x/document">` would have been simpler and
 * sends no credentials, so the route would have had to become reachable without
 * a session — a strange way to publish somebody's invoices.
 *
 * THE BLOB URL IS REVOKED when the screen goes away. A blob left unrevoked keeps
 * its bytes for the life of the tab, and these bytes are a customer's invoice.
 *
 * **THE INSPECTION PANEL CARRIES FACTS, NOT CLAIMS.** Stitch's right rail
 * asserts PDF/A-3b conformance, a qualified electronic seal, an RFC 3161
 * timestamp and eighteen passed pre-flight rules. The renderer makes none of
 * those claims. What it does produce — a file name, a media type, a byte count
 * and a SHA-256 — is exactly what a delivery record stores, so the panel shows
 * those and somebody holding a customer's complaint can compare them.
 */
export function InvoiceDocumentScreen({
  gateway,
  invoiceId,
  canSend,
  onBack,
  onSend,
}: {
  readonly gateway: FinanceGateway;
  readonly invoiceId: string;
  readonly canSend: boolean;
  readonly onBack: () => void;
  readonly onSend: () => void;
}) {
  const { state, reload } = useAsync<{
    document: InvoiceDocument;
    invoice: Invoice | null;
  }>(
    async () => ({
      document: await gateway.invoiceDocument(invoiceId),
      // The invoice itself, so the header can say which document this is. A
      // refusal costs the header line, not the document.
      invoice: await gateway
        .invoice(invoiceId)
        .then((result) => result.invoice)
        .catch(() => null),
    }),
    [gateway, invoiceId],
  );

  const document = state.status === 'ready' ? state.value.document : undefined;
  const blobUrl = useMemo(() => {
    if (document === undefined) return undefined;
    const binary = atob(document.base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return URL.createObjectURL(new Blob([bytes], { type: document.contentType }));
  }, [document]);

  useEffect(
    () => () => {
      if (blobUrl !== undefined) URL.revokeObjectURL(blobUrl);
    },
    [blobUrl],
  );

  if (state.status === 'loading') return <LoadingState title="Rendering the document…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (document === undefined || blobUrl === undefined) return null;

  const invoice = state.status === 'ready' ? state.value.invoice : null;
  const isDraft = invoice !== null && invoice.status === 'draft';

  return (
    <>
      <PageHeader
        actions={
          <>
            <Button
              variant="secondary"
              icon={<Icon name="caret-left" size={14} />}
              onClick={onBack}
            >
              Back to invoice
            </Button>
            {canSend && !isDraft && (
              <Button
                variant="primary"
                icon={<Icon name="arrow-square-out" size={14} />}
                onClick={onSend}
              >
                Proceed to send
              </Button>
            )}
          </>
        }
      />

      <IdentityCard
        name={invoice?.invoiceNumber ?? document.fileName}
        chips={
          <>
            {invoice !== null && (
              <StatusChip tone={statusTone(invoice.status)}>{invoice.status}</StatusChip>
            )}
            {isDraft && <StatusChip tone="warning">Watermarked as a draft</StatusChip>}
          </>
        }
        meta={
          invoice === null ? undefined : (
            <>
              <span>Issued</span>
              <MetaStrong>{formatDay(invoice.issueDate)}</MetaStrong>
              <MetaSeparator />
              <span>Total</span>
              <MetaStrong>{money(invoice.grossTotal, invoice.currency)}</MetaStrong>
            </>
          )
        }
      />

      <Split>
        <SplitMain span={8}>
          {/*
            THE VIEWER TOOLBAR. Stitch draws zoom controls, page navigation, an
            ISO margin grid and a font-outline inspector. The browser's own PDF
            viewer inside the frame owns zoom and paging, and there is no
            pre-press inspector anywhere in this product; what is left is what
            this bar can truthfully say about the file it is showing.
          */}
          <ViewerBar>
            <ViewerGroup>
              <Icon name="tray" size={15} />
              <span>{document.fileName}</span>
              <PanelTag>{document.contentType}</PanelTag>
            </ViewerGroup>
            <ViewerGroup>
              <span>{kilobytes(document.byteSize)}</span>
              <a href={blobUrl} download={document.fileName}>
                Download PDF
              </a>
            </ViewerGroup>
          </ViewerBar>
          {/*
            THE VIEWER'S CHROME IS TURNED OFF, not reproduced. Chromium draws a
            dark toolbar and a page-thumbnail rail around an embedded PDF, and
            the reference draws a sheet of paper on a light stage. `#toolbar=0`
            and `#navpanes=0` are the PDF open parameters every viewer that has
            them reads; a viewer that ignores them shows its own chrome, which
            is a viewer's decision and not something to fake around.
          */}
          <DocumentStage
            src={`${blobUrl}#toolbar=0&navpanes=0&view=FitH`}
            title={`Invoice document ${document.fileName}`}
          />
        </SplitMain>

        <SplitSide span={4}>
          <Panel>
            <PanelHead title="Document integrity" icon="lock-simple">
              <StatusChip tone="success">Rendered from the ledger</StatusChip>
            </PanelHead>
            <FieldGrid columns={2}>
              <Field label="File name" span2>
                <FieldValue strong>{document.fileName}</FieldValue>
              </Field>
              <Field label="Media type">
                <FieldValue strong>{document.contentType}</FieldValue>
              </Field>
              <Field label="Size">
                <FieldValue strong>{kilobytes(document.byteSize)}</FieldValue>
              </Field>
              <Field label="SHA-256" span2>
                <FieldValue>{document.sha256}</FieldValue>
              </Field>
            </FieldGrid>
            {/*
              THE DIGEST IS SHOWN, and it is not decoration. It is the same
              digest a delivery record stores, so somebody holding a customer's
              complaint can compare what this screen renders today against what
              was actually sent.
            */}
            <PanelNote icon="info">
              Every send records this digest. If a customer disputes what they received, compare
              theirs against the one on the delivery record.
            </PanelNote>
          </Panel>

          <Panel>
            <PanelHead title="What this document is" icon="info" />
            <PanelNote icon="check">
              Assembled from the stored invoice, the entity&rsquo;s document settings and the
              customer&rsquo;s party record. Nothing on it is typed here, and nothing about it is
              computed here.
            </PanelNote>
            {isDraft && (
              <Alert tone="warning" title="This is a draft">
                A draft carries no number and no tax point, so it is rendered watermarked. Issue the
                invoice before sending it.
              </Alert>
            )}
          </Panel>
        </SplitSide>
      </Split>
    </>
  );
}

// ---- Screen 32: send --------------------------------------------------------

/**
 * SENDING IT — `32_Send_Invoice/code.html`. The one screen in Finance whose
 * button cannot be undone.
 *
 * The reference's anatomy: a status strip naming the customer, the gross payable
 * and the terms, then a seven/five split with the dispatch configuration on the
 * left and a live preview of the message on the right.
 *
 * THE OUTCOME IS REPORTED AS `queued`, NEVER AS SENT. A worker contacts the mail
 * server, so at the moment this screen answers, nothing has left. Saying "sent"
 * here would be the easiest lie in the product to tell and the hardest to notice.
 *
 * THE RECIPIENT IS PREFILLED AND EDITABLE, in that order. The customer's billing
 * email is the usual destination; the person sending this invoice is the one who
 * knows whether this one goes to the buyer who is chasing it instead.
 *
 * ONE CHANNEL, DRAWN AS ONE. Stitch offers email, PEPPOL and a customer portal
 * in parallel, with read receipts and click telemetry. There is one channel and
 * no telemetry, so there is one channel row and it carries no switch — a switch
 * with nothing to switch to is a decoration that looks like a choice.
 */
export function SendInvoiceScreen({
  gateway,
  invoiceId,
  onSent,
  onCancel,
}: {
  readonly gateway: FinanceGateway;
  readonly invoiceId: string;
  readonly onSent: () => void;
  readonly onCancel: () => void;
}) {
  const { state, reload } = useAsync<{
    readonly invoice: Invoice;
    readonly customer: FinanceCustomer | null;
    readonly terms: readonly FinancePaymentTerm[];
    readonly document: InvoiceDocument | null;
  }>(async () => {
    const { invoice } = await gateway.invoice(invoiceId);
    const customers = await gateway.customers(invoice.legalEntityId).catch(() => []);
    return {
      invoice,
      customer: customers.find((entry) => entry.partyId === invoice.partyId) ?? null,
      terms: await gateway.paymentTerms(invoice.legalEntityId).catch(() => []),
      // The attachment's own facts. A refusal costs the attachment card; it does
      // not stop a send, because the server attaches the document itself.
      document: await gateway.invoiceDocument(invoiceId).catch(() => null),
    };
  }, [gateway, invoiceId]);

  /**
   * THE PREFILL IS A DEFAULT, NOT AN EFFECT.
   *
   * It used to be a `useEffect` that wrote the customer's email into state once
   * the fetch resolved, which meant the field existed EMPTY for one commit and
   * then filled. Nobody could type into that gap, but a reader could see it and
   * a test could read it, and neither should have to. Now the typed value is
   * `undefined` until somebody types, and what renders until then is the
   * server's own answer — so the field is never briefly wrong.
   */
  const [typed, setTyped] = useState<
    { recipient: string; subject: string; message: string } | undefined
  >(undefined);
  const [queued, setQueued] = useState<InvoiceDelivery | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [sending, setSending] = useState(false);

  const ready = state.status === 'ready' ? state.value : undefined;
  const form = typed ?? {
    recipient: ready?.customer?.billingEmail ?? '',
    subject:
      ready == null || ready.invoice.invoiceNumber === null
        ? ''
        : `Invoice ${ready.invoice.invoiceNumber}`,
    message: '',
  };
  const { recipient, subject, message } = form;
  const change = (patch: Partial<typeof form>) => {
    setTyped({ ...form, ...patch });
  };

  async function send(): Promise<void> {
    setSending(true);
    setError(undefined);
    try {
      setQueued(
        await gateway.sendInvoice(invoiceId, {
          recipient,
          ...(subject.trim() === '' ? {} : { subject }),
          ...(message.trim() === '' ? {} : { message }),
        }),
      );
    } catch (failure) {
      setError(refusalText(failure, 'The invoice could not be queued for sending.'));
    } finally {
      setSending(false);
    }
  }

  if (state.status === 'loading') return <LoadingState title="Loading invoice…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (ready === undefined) return null;

  if (queued !== undefined) {
    return (
      <>
        <PageHeader
          actions={
            <Button variant="primary" onClick={onSent}>
              View delivery history
            </Button>
          }
        />
        <Alert tone="success" title="Queued for sending">
          The attempt is recorded and a worker will hand the message to the mail server. Nothing has
          reached the customer yet — the delivery history is where the outcome appears, and it will
          say whether the server accepted it.
        </Alert>
        <Panel>
          <PanelHead title="What was queued" icon="check-circle" />
          <FieldGrid columns={2}>
            <Field label="To">
              <FieldValue strong>{queued.recipient}</FieldValue>
            </Field>
            <Field label="Subject">
              <FieldValue strong>{queued.subject}</FieldValue>
            </Field>
            <Field label="Attachment" span2>
              <FieldValue>
                {queued.documentFileName} · {kilobytes(queued.documentByteSize)}
              </FieldValue>
            </Field>
            <Field label="Digest" span2>
              <FieldValue>{queued.documentSha256}</FieldValue>
            </Field>
          </FieldGrid>
        </Panel>
      </>
    );
  }

  const { invoice, customer, terms, document } = ready;
  const term = terms.find((entry) => entry.id === customer?.paymentTermId);
  const isDraft = invoice.invoiceNumber === null;
  const entity = customer?.displayName ?? 'this customer';
  const previewSubject =
    subject.trim() === '' ? `Invoice ${invoice.invoiceNumber ?? '(draft)'}` : subject;
  const previewBody =
    message.trim() === ''
      ? `Please find attached invoice ${invoice.invoiceNumber ?? '(draft)'} for ${money(
          invoice.grossTotal,
          invoice.currency,
        )}, due ${formatDay(invoice.dueDate)}.`
      : message;

  return (
    <>
      <PageHeader
        actions={
          <>
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={<Icon name="arrow-square-out" size={14} />}
              disabled={sending || recipient.trim() === '' || isDraft}
              onClick={() => {
                void send();
              }}
            >
              {sending ? 'Queueing…' : 'Send and dispatch'}
            </Button>
          </>
        }
      />

      <IdentityCard
        mark={customer === null ? undefined : initialsOf(customer.displayName)}
        name={customer?.displayName ?? 'Outside your customer list'}
        {...(invoice.invoiceNumber === null ? {} : { code: invoice.invoiceNumber })}
        chips={<StatusChip tone={statusTone(invoice.status)}>{invoice.status}</StatusChip>}
        meta={
          <>
            <span>Gross payable</span>
            <MetaStrong>{money(invoice.grossTotal, invoice.currency)}</MetaStrong>
            <MetaSeparator />
            <span>{term?.name ?? 'Entity default'}</span>
            <MetaSeparator />
            <span>Due</span>
            <MetaStrong>{formatDay(invoice.dueDate)}</MetaStrong>
          </>
        }
      />

      {isDraft && (
        <Alert tone="warning" title="This invoice is still a draft">
          Only an issued invoice can be sent: a draft has no number for the customer to pay against.
          Issue it first.
        </Alert>
      )}
      {error !== undefined && (
        <Alert tone="danger" title="Not sent">
          {error}
        </Alert>
      )}

      <Split>
        <SplitMain span={7}>
          <Panel>
            <PanelHead title="Delivery channel" icon="arrow-square-out">
              <PanelTag>One channel</PanelTag>
            </PanelHead>
            <Channel
              title="Email with the document attached"
              icon="check-circle"
              tone="success"
              chip={<StatusChip tone="info">SMTP</StatusChip>}
              detail={
                customer?.billingEmail == null
                  ? 'This customer has no billing email recorded, so there is nothing to prefill.'
                  : `Default recipient: ${customer.billingEmail}`
              }
            />
            <PanelNote icon="info">
              There is no PEPPOL access point, no customer portal and no read-receipt tracking in
              this product, so none of those is offered here.
            </PanelNote>
          </Panel>

          <Panel>
            <PanelHead title="Email composition" icon="list" />
            <Form
              onSubmit={(event) => {
                event.preventDefault();
                void send();
              }}
            >
              <FormSection title="Recipient and wording">
                <TextField
                  label="To"
                  type="email"
                  value={recipient}
                  onChange={(event) => {
                    change({ recipient: event.target.value });
                  }}
                  helper={
                    customer?.billingEmail == null
                      ? 'This customer has no billing email recorded, so there is nothing to prefill.'
                      : "The customer's billing email. Change it to send this one somewhere else."
                  }
                  required
                />
                <TextField
                  label="Subject"
                  value={subject}
                  onChange={(event) => {
                    change({ subject: event.target.value });
                  }}
                  helper="Left empty, the server uses the invoice number and your entity's name."
                  optional
                />
                <TextArea
                  label="Message"
                  value={message}
                  rows={6}
                  onChange={(event) => {
                    change({ message: event.target.value });
                  }}
                  helper="Left empty, a short covering note is used. The invoice PDF is attached either way, and it — not this note — states what is owed."
                  optional
                />
              </FormSection>
            </Form>
          </Panel>

          <Panel>
            <PanelHead label="Attached payload" />
            {document === null ? (
              <PanelNote icon="warning">
                The document could not be rendered for preview here. The server attaches it itself
                when the send is queued.
              </PanelNote>
            ) : (
              <Attachment
                name={document.fileName}
                meta={`${document.contentType} · ${kilobytes(document.byteSize)} · SHA-256 ${document.sha256.slice(0, 16)}…`}
              />
            )}
            <PanelNote icon="info">
              One attachment. There is no electronic-invoice payload, no supporting-document upload
              and no timesheet import behind this screen.
            </PanelNote>
          </Panel>
        </SplitMain>

        {/* ---- the live preview, `lg:col-span-5` --------------------------- */}
        <SplitSide span={5}>
          <Panel variant="flush">
            <MailEnvelope>
              <MailField label="To">{recipient === '' ? 'Not set' : recipient}</MailField>
              <MailField label="Subject">{previewSubject}</MailField>
              <MailField label="Attach">
                {document === null ? 'The rendered invoice' : document.fileName}
              </MailField>
            </MailEnvelope>
            <MailBody>
              <MailBrand name="TESSMA ONE" note="Tax invoice dispatch" />
              <MailText>Dear {entity},</MailText>
              <MailText muted>{previewBody}</MailText>
              <MailSummary
                label={`Amount due (${invoice.currency})`}
                chip={<StatusChip tone={statusTone(invoice.status)}>{invoice.status}</StatusChip>}
                total={money(invoice.grossTotal, invoice.currency)}
                split={`Net ${money(invoice.netTotal, invoice.currency)} · VAT ${money(
                  invoice.taxTotal,
                  invoice.currency,
                )}`}
                foot={`Issued ${formatDay(invoice.issueDate)} · due ${formatDay(invoice.dueDate)}`}
              />
              {document !== null && (
                <Attachment name={document.fileName} meta={kilobytes(document.byteSize)} />
              )}
              <MailFooter>
                <span>
                  The exact wording, letterhead and bank details are the server&rsquo;s, taken from
                  your Finance document settings.
                </span>
              </MailFooter>
            </MailBody>
          </Panel>
          <Panel>
            <PanelHead title="What is recorded" icon="lock-simple" />
            <PanelNote icon="info">
              Every attempt is written to the delivery register with the recipient, the subject, the
              attachment&rsquo;s digest and who asked for it — before the mail server is contacted,
              so a send that fails is still on the record.
            </PanelNote>
          </Panel>
        </SplitSide>
      </Split>
    </>
  );
}

// ---- Screen 33: the register ------------------------------------------------

/**
 * WHAT HAS GONE OUT, AND WHAT DID NOT — `33_Delivery_History/code.html`.
 *
 * The reference's anatomy: an action strip, a four-card metric bar, a filter
 * toolbar with status pills, then an eight/four split holding the register and
 * an audit drawer for whichever row is selected.
 *
 * FOUR COUNTS, FROM THE ROWS THEMSELVES. The Stitch design's four cards are
 * dispatch volume, success rate, PEPPOL parity and settlement velocity; the last
 * two are facts nothing here observes. The composition is kept and filled with
 * four counts that are true of the same data — attempts, handed over, failed,
 * still queued. Counting rows is not accounting: there is no money in any of
 * them, and the alternative was inventing two metrics.
 *
 * THE DRAWER IS THE ROW'S OWN RECORD. Stitch's telemetry timeline has seven
 * events including a mailbox handshake, a customer open and an open-banking
 * link. Three things are actually recorded — the request, the outcome, and the
 * document that went with it — so three is what the timeline has.
 *
 * THE DIGEST IS THE POINT OF THE TABLE. A customer saying they never got an
 * invoice, or got a different one, is answered by comparing that against a
 * re-render.
 */
export function DeliveryHistoryScreen({
  gateway,
  legalEntityId,
  onOpenInvoice,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId: string;
  readonly onOpenInvoice: (invoiceId: string) => void;
}) {
  const [stateFilter, setStateFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const { state, reload } = useAsync<readonly InvoiceDelivery[]>(
    () => gateway.deliveryRegister(legalEntityId),
    [gateway, legalEntityId],
  );

  if (state.status === 'loading') return <LoadingState title="Loading delivery history…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const all = state.value;
  const counts = {
    attempts: all.length,
    sent: all.filter((row) => row.state === 'sent').length,
    failed: all.filter((row) => row.state === 'failed').length,
    queued: all.filter((row) => row.state === 'queued').length,
  };

  const header = (
    <PageHeader
      actions={
        <Button
          variant="secondary"
          icon={<Icon name="arrows-down-up" size={14} />}
          onClick={() => {
            reload();
          }}
        >
          Refresh
        </Button>
      }
    />
  );

  const summary = (
    <SummaryBand>
      <SummaryCard
        icon="tray"
        tone="teal"
        label="Attempts"
        value={String(counts.attempts)}
        caption="Sends asked for"
      />
      <SummaryCard
        icon="check-circle"
        tone="success"
        label="Handed to mail server"
        value={String(counts.sent)}
        caption="Accepted, not confirmed"
      />
      <SummaryCard
        icon="warning"
        urgent
        label="Failed"
        value={String(counts.failed)}
        caption="Each carries its reason"
      />
      <SummaryCard
        icon="calendar-blank"
        tone="info"
        label="Still queued"
        value={String(counts.queued)}
        caption="Waiting, or stuck"
      />
    </SummaryBand>
  );

  if (all.length === 0) {
    return (
      <>
        {header}
        {summary}
        <EmptyState
          icon="tray"
          title="Nothing sent yet"
          description="No invoice has been emailed out of this legal entity."
        />
      </>
    );
  }

  const needle = query.trim().toLowerCase();
  const shown = all.filter((row) => {
    if (stateFilter !== 'all' && row.state !== stateFilter) return false;
    if (needle === '') return true;
    return [row.invoiceNumber ?? '', row.customerName ?? '', row.recipient, row.subject]
      .join(' ')
      .toLowerCase()
      .includes(needle);
  });
  const selected = all.find((row) => row.id === selectedId) ?? shown[0] ?? all[0];

  return (
    <>
      {header}
      {summary}

      <Split>
        <SplitMain span={8}>
          <RecordsCard>
            <TabStrip
              label="Dispatch state"
              active={stateFilter}
              onSelect={setStateFilter}
              tabs={[
                { id: 'all', label: 'All dispatches', count: counts.attempts },
                { id: 'sent', label: 'Handed to mail server', count: counts.sent },
                { id: 'queued', label: 'Queued', count: counts.queued },
                { id: 'failed', label: 'Failed', count: counts.failed },
              ]}
            />
            <FilterBar flush>
              <FilterSearch
                label="Search the register"
                placeholder="Search by invoice number, customer, recipient or subject…"
                value={query}
                onChange={setQuery}
              />
            </FilterBar>
            <RecordsScroll dense>
              <Table label="Document dispatch register">
                <thead>
                  {/*
                    FIVE COLUMNS, BECAUSE THE DRAWER IS THE SIXTH.
                    Stitch draws seven in a card that is eight of twelve
                    columns, and at 1280 that is 630 pixels — a 64-character
                    digest and a requester's name do not fit beside a timestamp,
                    a document, a recipient and a state, and the ones that did
                    not fit were pushed off the right edge of a card whose
                    scrollbar the shell hides. Both are in the audit panel
                    beside the table, in full, which is where the reference puts
                    the detail of the row you are looking at.
                  */}
                  <Tr>
                    <Th>Requested</Th>
                    <Th>Document, counterparty &amp; recipient</Th>
                    <Th>State</Th>
                    <Th align="right">Audit</Th>
                  </Tr>
                </thead>
                <tbody>
                  {shown.map((row) => (
                    <Tr key={row.id} selected={row.id === selected?.id}>
                      <Td variant="secondary">{formatWhen(row.requestedAt)}</Td>
                      <Td>
                        <EntityCell
                          name={row.invoiceNumber ?? 'Invoice'}
                          {...(row.customerName == null ? {} : { sub: row.customerName })}
                          onOpen={() => {
                            onOpenInvoice(row.invoiceId);
                          }}
                        />
                        {/*
                          THE ADDRESS UNDER THE DOCUMENT, not in a column of its
                          own. This card is eight of twelve columns — 630 pixels
                          at 1280 — and a column for an email address cost the
                          table 54 of them, which the shell's hidden scrollbars
                          turned into a silently missing column rather than a
                          tight one. It is the same fact in the same row, and
                          the audit panel beside it repeats it in full.
                        */}
                        <RowDetail>{row.recipient}</RowDetail>
                      </Td>
                      <Td>
                        <StatusChip tone={DELIVERY_TONE[row.state]}>
                          {DELIVERY_STATE[row.state]}
                        </StatusChip>
                        {/*
                          THE REASON A FAILURE FAILED IS ON THE ROW. Stitch puts
                          telemetry under the status chip; the one piece of
                          telemetry this platform has is the mail server's own
                          refusal, and it belongs where somebody scanning for
                          red will read it — not only in the panel beside it.
                        */}
                        {row.error !== null && <RowDetail>{row.error}</RowDetail>}
                      </Td>
                      <Td>
                        <RowActions>
                          <RowAction
                            icon="list"
                            label={`Inspect the dispatch of ${row.invoiceNumber ?? 'this invoice'}`}
                            onClick={() => {
                              setSelectedId(row.id);
                            }}
                          />
                        </RowActions>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </RecordsScroll>
            <RecordsFooter>
              <span>
                Showing <RecordsCount>{shown.length}</RecordsCount> of{' '}
                <RecordsCount>{all.length}</RecordsCount> dispatches
              </span>
              <span>Newest request first</span>
            </RecordsFooter>
          </RecordsCard>
        </SplitMain>

        <SplitSide span={4}>
          {selected === undefined ? (
            <Panel>
              <PanelHead title="Dispatch audit" icon="lock-simple" />
              <PanelNote icon="info">Choose a row to inspect what was sent.</PanelNote>
            </Panel>
          ) : (
            <Panel>
              <PanelHead title={selected.invoiceNumber ?? 'Dispatch'} icon="lock-simple">
                <StatusChip tone={DELIVERY_TONE[selected.state]}>
                  {DELIVERY_STATE[selected.state]}
                </StatusChip>
              </PanelHead>
              <FieldGrid columns={2}>
                <Field label="Counterparty" span2>
                  <FieldValue strong>{selected.customerName ?? '—'}</FieldValue>
                </Field>
                <Field label="Recipient" span2>
                  <FieldValue strong>{selected.recipient}</FieldValue>
                </Field>
                <Field label="Subject" span2>
                  <FieldValue>{selected.subject}</FieldValue>
                </Field>
                <Field label="Attachment">
                  <FieldValue strong>{selected.documentFileName}</FieldValue>
                </Field>
                <Field label="Size">
                  <FieldValue strong>{kilobytes(selected.documentByteSize)}</FieldValue>
                </Field>
                <Field label="SHA-256" span2>
                  <FieldValue>{selected.documentSha256}</FieldValue>
                </Field>
                <Field label="Asked for by" span2>
                  <FieldValue strong>{selected.requestedByName ?? selected.requestedBy}</FieldValue>
                </Field>
                <Field label="Correlation id" span2>
                  <FieldValue>{selected.correlationId}</FieldValue>
                </Field>
              </FieldGrid>

              <PanelHead label="Dispatch sequence" />
              <Timeline>
                <TimelineEvent
                  tone="info"
                  when={formatWhen(selected.requestedAt)}
                  source="Requested"
                  title="Send requested"
                >
                  By {selected.requestedByName ?? selected.requestedBy}. The document was rendered
                  and its digest recorded before the mail server was contacted.
                </TimelineEvent>
                {selected.settledAt === null ? (
                  <TimelineEvent
                    tone="neutral"
                    when="Not yet"
                    source="Pending"
                    title="Waiting for the worker"
                  >
                    Nothing has been handed over yet. A request that stays here is stuck, not
                    delivered.
                  </TimelineEvent>
                ) : (
                  <TimelineEvent
                    tone={DELIVERY_ACCENT[selected.state]}
                    when={formatWhen(selected.settledAt)}
                    source={selected.state === 'failed' ? 'Refused' : 'Accepted'}
                    title={DELIVERY_STATE[selected.state]}
                  >
                    {selected.state === 'failed'
                      ? (selected.error ?? 'The mail server refused it and gave no reason.')
                      : `The mail server accepted the message${
                          selected.providerMessageId === null
                            ? ''
                            : ` as ${selected.providerMessageId}`
                        }. That is not a receipt — nothing here observes a mailbox.`}
                  </TimelineEvent>
                )}
              </Timeline>

              <PanelFoot>
                <PanelNote icon="info">
                  This is the whole record. There is no proof-of-delivery artefact to download and
                  no acknowledgement to wait for.
                </PanelNote>
              </PanelFoot>
            </Panel>
          )}
        </SplitSide>
      </Split>
    </>
  );
}

/** The per-invoice history, shown on Screen 06 rather than as a page of its own. */
export function InvoiceDeliveriesPanel({
  gateway,
  invoiceId,
}: {
  readonly gateway: FinanceGateway;
  readonly invoiceId: string;
}) {
  const { state, reload } = useAsync<readonly InvoiceDelivery[]>(
    () => gateway.invoiceDeliveries(invoiceId),
    [gateway, invoiceId],
  );

  if (state.status === 'loading') return <LoadingState title="Loading delivery history…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  return (
    <Panel>
      <PanelHead title="Dispatch history" icon="arrow-square-out">
        <PanelTag>
          {state.value.length} {state.value.length === 1 ? 'attempt' : 'attempts'}
        </PanelTag>
      </PanelHead>
      {state.value.length === 0 ? (
        <PanelNote icon="info">This invoice has not been emailed.</PanelNote>
      ) : (
        <Timeline>
          {state.value.map((row) => (
            <TimelineEvent
              key={row.id}
              tone={DELIVERY_ACCENT[row.state]}
              when={formatWhen(row.requestedAt)}
              source={DELIVERY_STATE[row.state]}
              title={`To ${row.recipient}`}
            >
              {row.state === 'failed'
                ? (row.error ?? 'The mail server refused it and gave no reason.')
                : `Asked for by ${row.requestedByName ?? row.requestedBy}${
                    row.providerMessageId === null ? '' : ` · ${row.providerMessageId}`
                  }`}
            </TimelineEvent>
          ))}
        </Timeline>
      )}
    </Panel>
  );
}
